import { isSet } from './CommonFunctions';

/**
 * Dedupe key for configuration pickers: physical column first, then API name, then label+type.
 */
export const getWindowFieldDedupeKey = (field = {}) => {
    // Inner-rating variable copies share variable_name with the parent's own variables.
    const relationalId = isSet(field.relational_id, isSet(field.relationalId, ''));
    if (field.is_variable_or_constant === true && relationalId !== '' && isSet(field.id, '') !== '') {
        return `var:${field.id}`;
    }
    const columnDescriptorId = isSet(field.column_descriptor_id, '');
    if (columnDescriptorId !== '') {
        return `cd:${columnDescriptorId}`;
    }
    const apiFieldName = isSet(field.api_field_name, '');
    if (apiFieldName !== '') {
        return `api:${apiFieldName.trim().toLowerCase()}`;
    }
    const isSystemField = field.is_system_field === true || field.system_field === true;
    const fieldName = isSet(field.field_name, '');
    const fieldType = isSet(field.field_type, '');
    if (!isSystemField && fieldName !== '' && fieldType !== '') {
        return `fn:${fieldType.trim().toLowerCase()}:${fieldName.trim().toLowerCase()}`;
    }
    const id = isSet(field.id, '');
    return id !== '' ? `td:${id}` : '';
};

const mergeLayoutFieldVariants = (existingVariants = [], incomingVariants = [], incomingField = {}) => {
    const merged = [...existingVariants];
    const hasVariant = (tableDescriptorId) =>
        merged.some((variant) => isSet(variant.table_descriptor_id, '') === tableDescriptorId);

    incomingVariants.forEach((variant) => {
        const tableDescriptorId = isSet(variant.table_descriptor_id, '');
        if (tableDescriptorId !== '' && !hasVariant(tableDescriptorId)) {
            merged.push(variant);
        }
    });

    const incomingId = isSet(incomingField.id, '');
    if (incomingId !== '' && !hasVariant(incomingId)) {
        merged.push({
            table_descriptor_id: incomingId,
            display_name: isSet(incomingField.field_name, ''),
            layout_id: isSet(incomingField.layout_id, null),
            layout_name: isSet(incomingField.layout_name, null),
        });
    }

    return merged;
};

/**
 * Field labels that appear more than once in a picker list (case-insensitive).
 * Used only to disambiguate same-named fields from different layouts.
 */
export const buildDuplicateFieldNameSet = (windowFields = []) => {
    const counts = {};
    if (!Array.isArray(windowFields)) {
        return new Set();
    }
    windowFields.forEach((field) => {
        if (!field) {
            return;
        }
        const name = isSet(field.field_name, '').trim().toLowerCase();
        if (name === '' || name === '-none-') {
            return;
        }
        counts[name] = (counts[name] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter((name) => counts[name] > 1));
};

/**
 * One picker entry per physical window column (layout copies collapse to a single row).
 */
export const dedupeWindowFieldsByColumnDescriptor = (windowFields = []) => {
    if (!Array.isArray(windowFields) || windowFields.length === 0) {
        return windowFields;
    }

    const deduped = new Map();
    windowFields.forEach((field) => {
        if (!field) {
            return;
        }
        const key = getWindowFieldDedupeKey(field);
        if (key === '') {
            return;
        }

        const existing = deduped.get(key);
        if (!existing) {
            deduped.set(key, {
                ...field,
                layout_field_variants: [...isSet(field.layout_field_variants, [])],
            });
            return;
        }

        deduped.set(key, {
            ...existing,
            layout_field_variants: mergeLayoutFieldVariants(
                isSet(existing.layout_field_variants, []),
                isSet(field.layout_field_variants, []),
                field
            ),
        });
    });

    return Array.from(deduped.values());
};

/**
 * Resolve a display layout label for picker hints.
 */
export const getWindowFieldLayoutLabel = (field = {}) => {
    const direct = isSet(field.layout_name, '').trim();
    if (direct !== '') {
        return direct;
    }
    const variants = isSet(field.layout_field_variants, []);
    if (Array.isArray(variants) && variants.length > 0) {
        const fromVariant = isSet(variants[0].layout_name, '').trim();
        if (fromVariant !== '') {
            return fromVariant;
        }
    }
    return '';
};

/**
 * Normalize window-basic-fields payload before storing in Redux.
 */
export const normalizeWindowBasicFieldsResponse = (responseData = {}, preparePickList = (fields) => fields) => {
    const fieldsWithFilters = isSet(isSet(responseData, {}).fields_with_filters, { window_fields: [] });
    const dedupedFields = dedupeWindowFieldsByColumnDescriptor(
        isSet(fieldsWithFilters.window_fields, [])
    ).map((field) => {
        if (!field) {
            return field;
        }
        const layoutName = getWindowFieldLayoutLabel(field);
        if (layoutName === '' || isSet(field.layout_name, '') !== '') {
            return field;
        }
        return { ...field, layout_name: layoutName };
    });
    const preparedFields = preparePickList(dedupedFields);
    return {
        ...responseData,
        fields_with_filters: {
            ...fieldsWithFilters,
            window_fields: preparedFields,
        },
    };
};
